import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import type { CustomerDTO } from "@ifa/utils";
import { useBeforeUnload } from "react-router-dom";
import { FnaTabs } from "./FnaTabs";
import {
  FnaBasicInfoPlaceholder,
  type FnaBasicInfoHandle,
} from "./content/FnaBasicInfoPlaceholder";
import { FnaNeedsAnalysisPlaceholder } from "./content/FnaNeedsAnalysisPlaceholder";
import { FnaHistoryPlaceholder } from "./content/FnaHistoryPlaceholder";
import type { FinancialSnapshot } from "./content/fnaBasicConfig";
import styles from "./FnaSection.module.css";

type FnaSectionProps = {
  customer: CustomerDTO | null;
  onDirtyChange?: (dirty: boolean) => void;
  mode: "view" | "edit";
};

export type FnaSectionHandle = {
  save: () => FinancialSnapshot | undefined;
  cancel: () => void;
  isDirty: () => boolean;
};

export const FnaSection = forwardRef<FnaSectionHandle, FnaSectionProps>(function FnaSection(
  { customer, onDirtyChange, mode }: FnaSectionProps,
  ref
) {
  const [activeTab, setActiveTab] = useState<"basic" | "analysis" | "history">(
    "basic"
  );
  const [isDirty, setIsDirty] = useState(false);
  const basicRef = useRef<FnaBasicInfoHandle>(null);

  // 将 OrderFinancialVO 转换为 FinancialSnapshot
  const financialData = useMemo((): FinancialSnapshot | undefined => {
    if (!customer?.financial) return undefined;

    const financial = customer.financial;
    return {
      income: financial.income,
      expense: financial.expense,
      currentAssets: financial.currentAssets,
      nonCurrentAssets: financial.nonCurrentAssets,
      liabilities: financial.liabilities,
      protection: financial.protection,
    };
  }, [customer?.financial]);

  const tabs = useMemo(
    () => [
      { key: "basic" as const, label: "基本財務資料" },
      { key: "analysis" as const, label: "財務需要分析" },
      { key: "history" as const, label: "歷史簽署的 FNA 文件" },
    ],
    []
  );

  useBeforeUnload((event) => {
    if (!isDirty) return;
    event.preventDefault();
    event.returnValue = "";
  });

  useEffect(() => {
    setIsDirty(false);
  }, [customer]);

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  useImperativeHandle(
    ref,
    () => ({
      save: () => basicRef.current?.save(),
      cancel: () => basicRef.current?.cancel(),
      isDirty: () => isDirty,
    }),
    [isDirty]
  );

  const handleTabChange = (key: "basic" | "analysis" | "history") => {
    if (isDirty) {
      const proceed = window.confirm("當前有未儲存的內容，切換將會丟失。確認切換？");
      if (!proceed) return;
      setIsDirty(false);
    }
    setActiveTab(key);
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case "basic":
        return (
          <FnaBasicInfoPlaceholder
            ref={basicRef}
            mode={mode}
            initialData={financialData}
            onEditingChange={setIsDirty}
          />
        );
      case "analysis":
        return <FnaNeedsAnalysisPlaceholder
          financialAnalysis={customer?.financial?.financialAnalysis}
          chooseProducts={customer?.financial?.chooseProducts}
          sourceOrderId={customer?.sourceOrderId}
        />;
      case "history":
        return <FnaHistoryPlaceholder historyFnaList={customer?.historyFnaList} />;
      default:
        return null;
    }
  };

  return (
    <div className={styles.fnaSection}>
      <FnaTabs tabs={tabs} activeKey={activeTab} onChange={handleTabChange} />

      <div className={styles.fnaBody}>
        <div className={styles.fnaMain}>{renderTabContent()}</div>
      </div>
    </div>
  );
});
